import React, { useState } from "react";
import {
  CButton,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CAlert,
  CSelect,
  CFormGroup,
  CLabel,
  CSpinner,
  CInputCheckbox,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  deletePaymentMethodApi,
  setDefaultPaymentMethodApi,
} from "../../../apis/apis";
import { refreshPaymentMethodsPage } from "../refreshPaymentMethodsPage";
import { pushToast } from "../../Toaster/Toaster.slice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { isPrimaryPaymentMethod, isEligibleDefaultPaymentMethod, getDefaultEligibleAutopayPaymentMethodId, getAutopayPaymentMethodIneligibilityReason } from "../utils";
import { PaymentMethodActionTooltip } from "./PaymentMethodActionTooltip";
import ChangePaymentConfirmModal from "../../Dashboard/components/ChangePaymentConfirmModal";
import { buildAutopayEnrollmentPaymentMethodOptions } from "../../Dashboard/components/autopayPaymentMethodOptions";
import {
  formatAutopayApiError,
  isDelegateAutopayAccount,
  isModalActionCancelled,
  isOwnerEnrolledInAutopay,
  reassignAutopayPaymentMethodForAccounts,
  setAutopayPaymentMethodForAccount,
} from "../../Dashboard/components/setAutopayPaymentMethod";
import { ModalAccountList } from "../../../components/ModalAccountList";
import { DelegateAutopayInfoAlert } from "../../../components/DelegateAutopayInfoAlert";
import { PaymentMethodConfirmSummary } from "../../../components/PaymentMethodConfirmSummary";
import { PaymentMethodSourceLabel } from "./PaymentMethodSourceLabel";
import {
  formatAffectedServiceAccountsLabel,
  formatTruncatedAccountListSummary,
} from "../../../utils/accountListDisplay";

interface PropsInterface {
  columns: any;
  onPaymentMethodsChanged?: () => Promise<void>;
}

const ModalWarningAlert: React.FC<{
  title: string;
  className?: string;
  children: React.ReactNode;
}> = ({ title, className, children }) => (
  <CAlert color="warning" className={className}>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        columnGap: "12px",
        rowGap: "8px",
        alignItems: "center",
      }}
    >
      <CIcon
        name="cis-warning"
        size="lg"
        style={{ gridColumn: 1, gridRow: 1, flexShrink: 0 }}
      />
      <span style={{ gridColumn: 2, gridRow: 1, fontWeight: 600 }}>{title}</span>
      <div style={{ gridColumn: 2, gridRow: 2 }}>{children}</div>
    </div>
  </CAlert>
);

const ModalInfoAlert: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className, children }) => (
  <CAlert color="info" className={className}>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        columnGap: "12px",
        alignItems: "start",
      }}
    >
      <CIcon
        name="cis-info-circle"
        size="lg"
        style={{ gridColumn: 1, flexShrink: 0 }}
      />
      <div style={{ gridColumn: 2 }}>{children}</div>
    </div>
  </CAlert>
);

export const PaymentMethodsTable: React.FC<PropsInterface> = (props) => {
  const [removeConfirm, setRemoveConfirm]: any = useState(false);
  const [replacementPmId, setReplacementPmId] = useState("");
  const [replacementPmTouched, setReplacementPmTouched] = useState(false);
  const [reassignAutopayChecked, setReassignAutopayChecked] = useState(true);
  const [autopayReassignmentPmId, setAutopayReassignmentPmId] = useState("");
  const [primaryConfirm, setPrimaryConfirm]: any = useState(false);
  const [autopayChangeConfirm, setAutopayChangeConfirm]: any = useState(false);
  const [isRemoveSubmitting, setIsRemoveSubmitting] = useState(false);
  const [isPrimarySubmitting, setIsPrimarySubmitting] = useState(false);
  const [isAutopayChangeSubmitting, setIsAutopayChangeSubmitting] =
    useState(false);
  const dispatch = useAppDispatch();
  const { columns = [], onPaymentMethodsChanged } = props;
  const dataSource = useAppSelector((state) => state.payment.methods.data);

  const { data, activeTenantAccountIndex } = useAppSelector(
    (state) => state.accountSummary
  );
  const { defaultPaymentMethod } = useAppSelector(
    (state) => state.payment.methods
  );
  const accountSummaryData = data?.[activeTenantAccountIndex];
  const tenantAccountId = accountSummaryData?.id;
  const isDelegateAutopay = accountSummaryData
    ? isDelegateAutopayAccount(accountSummaryData)
    : false;
  const isOwnerAutopayEnrolled = accountSummaryData
    ? isOwnerEnrolledInAutopay(accountSummaryData)
    : false;
  const autopayPaymentMethodId = isOwnerAutopayEnrolled
    ? accountSummaryData?.autopay?.paymentMethodId
    : null;

  const toggleRemoveConfirm = (row?: any) => {
    if (isRemoveSubmitting) {
      return;
    }

    setRemoveConfirm(row || false);
    setReplacementPmTouched(false);
    setReassignAutopayChecked(true);
    if (!row) {
      setReplacementPmId("");
      setAutopayReassignmentPmId("");
      return;
    }

    const others = dataSource.filter((pm: any) => pm.id !== row.id);
    const needsReplacement =
      isPrimaryPaymentMethod(row, defaultPaymentMethod?.id) && others.length > 0;
    const eligibleOthers = others.filter(isEligibleDefaultPaymentMethod);
    const defaultReplacementId = getDefaultEligibleAutopayPaymentMethodId(others);
    setReplacementPmId(needsReplacement ? defaultReplacementId : "");
    setAutopayReassignmentPmId(
      eligibleOthers.length === 1 ? eligibleOthers[0].id : ""
    );
  };

  const refreshAfterPaymentMethodChange = async () => {
    if (onPaymentMethodsChanged) {
      await onPaymentMethodsChanged();
      return;
    }

    await refreshPaymentMethodsPage(dispatch);
  };

  const otherActivePaymentMethods = removeConfirm
    ? dataSource.filter((pm: any) => pm.id !== removeConfirm.id)
    : [];
  const eligibleReplacementPaymentMethods = otherActivePaymentMethods.filter(
    isEligibleDefaultPaymentMethod
  );
  const replacementPaymentMethodOptions =
    buildAutopayEnrollmentPaymentMethodOptions(
      eligibleReplacementPaymentMethods
    );

  const isRemovingPrimary =
    Boolean(removeConfirm) &&
    isPrimaryPaymentMethod(removeConfirm, defaultPaymentMethod?.id);
  const isLastPaymentMethod =
    Boolean(removeConfirm) && otherActivePaymentMethods.length === 0;
  const requiresReplacement =
    isRemovingPrimary && otherActivePaymentMethods.length > 0;
  const canReplaceDefault = eligibleReplacementPaymentMethods.length > 0;
  const showLastPaymentMethodWarning =
    isLastPaymentMethod && !requiresReplacement;

  const accountsUsingPmForAutopay =
    data?.filter(
      (account) =>
        account.autopay?.enabled &&
        account.autopay?.paymentMethodId === removeConfirm?.id
    ) ?? [];

  const showAutopayDisableWarning = accountsUsingPmForAutopay.length > 0;

  const onlyActiveAccountAffected =
    accountsUsingPmForAutopay.length === 1 &&
    accountsUsingPmForAutopay[0].id === accountSummaryData?.id;

  const affectedAccountsLabel = formatTruncatedAccountListSummary(
    accountsUsingPmForAutopay
  );

  const showDefaultReassignAutopayCheckbox =
    requiresReplacement &&
    accountsUsingPmForAutopay.length > 0 &&
    Boolean(replacementPmId);

  const showNonDefaultAutopayReassignment =
    !requiresReplacement &&
    accountsUsingPmForAutopay.length > 0 &&
    otherActivePaymentMethods.length > 0;

  const autopayReassignmentPaymentMethod = otherActivePaymentMethods.find(
    (pm: any) => pm.id === autopayReassignmentPmId
  );

  const replacementPaymentMethod = otherActivePaymentMethods.find(
    (pm: any) => pm.id === replacementPmId
  );

  const isRemoveFormValid =
    (!requiresReplacement || (!!replacementPmId && canReplaceDefault)) &&
  (!replacementPmId ||
    isEligibleDefaultPaymentMethod(
      otherActivePaymentMethods.find((pm: any) => pm.id === replacementPmId) ?? {
        type: "",
      }
    ));
  const showReplacementPmError =
    requiresReplacement && replacementPmTouched && !replacementPmId;

  const touchRemoveFormValidation = () => {
    if (!isRemoveFormValid) {
      setReplacementPmTouched(true);
    }
  };

  const willReassignAutopayOnRemove =
    Boolean(
      (requiresReplacement
        ? reassignAutopayChecked && replacementPmId
        : autopayReassignmentPmId) && accountsUsingPmForAutopay.length > 0
    );

  const getRemoveConfirmButtonLabel = () => {
    const actions = ["Remove"];
    if (requiresReplacement) {
      actions.push("Replace");
    }
    if (willReassignAutopayOnRemove) {
      actions.push("Reassign");
    }

    if (actions.length === 1) {
      return actions[0];
    }

    if (actions.length === 2) {
      return actions.join(" & ");
    }

    return `${actions.slice(0, -1).join(", ")} & ${actions[actions.length - 1]}`;
  };

  const handleRemoveConfirm = async () => {
    setIsRemoveSubmitting(true);
    try {
      if (requiresReplacement) {
        await setDefaultPaymentMethodApi(replacementPmId);
      }

      const autopayReassignmentPmIdForSubmit = requiresReplacement
        ? reassignAutopayChecked
          ? replacementPmId
          : ""
        : autopayReassignmentPmId;

      if (
        autopayReassignmentPmIdForSubmit &&
        accountsUsingPmForAutopay.length > 0
      ) {
        await reassignAutopayPaymentMethodForAccounts(
          dispatch,
          accountsUsingPmForAutopay.map((account) => account.id),
          autopayReassignmentPmIdForSubmit,
          { showToast: false, refresh: false }
        );
      }

      await deletePaymentMethodApi(removeConfirm.id);

      const reassignedAutopay =
        Boolean(autopayReassignmentPmIdForSubmit) &&
        accountsUsingPmForAutopay.length > 0;

      dispatch(
        pushToast({
          message: reassignedAutopay
            ? "Payment method removed and AutoPay reassigned for affected service accounts"
            : "Payment method successfully removed",
          type: "success",
        })
      ).catch((e: string) => {
        throw new Error(e);
      });
      await refreshAfterPaymentMethodChange();
      setRemoveConfirm(false);
      setReplacementPmId("");
      setReplacementPmTouched(false);
      setAutopayReassignmentPmId("");
      setReassignAutopayChecked(true);
    } catch (e: any) {
      console.error(e);
      dispatch(
        pushToast({
          message: `${
            e.response?.data?.message
              ? `${e.response?.data?.message}:`
              : "Error:"
          } ${e.message}`,
          type: "error",
        })
      ).catch((e: string) => {
        throw new Error(e);
      });
      await refreshAfterPaymentMethodChange();
    } finally {
      setIsRemoveSubmitting(false);
    }
  };

  const togglePrimaryConfirm = (row?: any) => {
    if (isPrimarySubmitting) {
      return;
    }

    setPrimaryConfirm(row || false);
  };

  const toggleAutopayChangeConfirm = (row?: any) => {
    if (
      isAutopayChangeSubmitting ||
      isDelegateAutopay ||
      (row && !isEligibleDefaultPaymentMethod(row))
    ) {
      return;
    }

    setAutopayChangeConfirm(row || false);
  };

  const handleAutopayChangeConfirm = async () => {
    if (!tenantAccountId || !autopayChangeConfirm?.id) {
      return;
    }

    setIsAutopayChangeSubmitting(true);
    try {
      await setAutopayPaymentMethodForAccount(
        dispatch,
        tenantAccountId,
        autopayChangeConfirm.id
      );
      setAutopayChangeConfirm(false);
    } catch (e: any) {
      console.error(e);
      if (!isModalActionCancelled(e)) {
        dispatch(
          pushToast({
            message: formatAutopayApiError(e),
            type: "error",
          })
        ).catch((err: string) => {
          throw new Error(err);
        });
      }
    } finally {
      setIsAutopayChangeSubmitting(false);
    }
  };

  const handlePrimaryConfirm = async () => {
    setIsPrimarySubmitting(true);
    try {
      await setDefaultPaymentMethodApi(primaryConfirm.id);
      dispatch(
        pushToast({
          message: "Default payment method successfully set",
          type: "success",
        })
      ).catch((e: string) => {
        throw new Error(e);
      });
      await refreshAfterPaymentMethodChange();
      setPrimaryConfirm(false);
    } catch (e: any) {
      console.error(e);
      dispatch(
        pushToast({
          message: `${
            e.response?.data?.message
              ? `${e.response?.data?.message}:`
              : "Error:"
          } ${e.message}`,
          type: "error",
        })
      ).catch((e: string) => {
        throw new Error(e);
      });
    } finally {
      setIsPrimarySubmitting(false);
    }
  };

  if (dataSource.length === 0) {
    return (
      <div className="card-body">
        {isDelegateAutopay && <DelegateAutopayInfoAlert />}
        No payment methods
      </div>
    );
  }

  const hiddenColumnsMobile = (key: any) =>
    (["status", "type", "expiryDate"].includes(key) && "d-md-down-none") || "";

  const getColumnClassName = (column: { key: string; className?: string }) =>
    [hiddenColumnsMobile(column.key), column.className].filter(Boolean).join(" ");

  return (
    <>
      {isDelegateAutopay && (
        <div className="card-body pb-0">
          <DelegateAutopayInfoAlert />
        </div>
      )}
      <table className="table table-striped table-hover">
        <thead>
          <tr>
            {columns.map((column: any) => {
              return (
                <th
                  scope="col"
                  key={column.key}
                  className={getColumnClassName(column)}
                >
                  {column.title}
                </th>
              );
            })}
            <th scope="col" className="payment-method-action-column">
              Default
            </th>
            {isOwnerAutopayEnrolled && (
              <th scope="col" className="payment-method-action-column">
                AutoPay
              </th>
            )}
            <th scope="col" className="payment-method-action-column">
              Remove
            </th>
          </tr>
        </thead>
        <tbody>
          {dataSource.map((data: any, idx: number) => {
            return (
              <tr key={idx}>
                {columns.map((column: any, columnIdx: number) => {
                  if (column.render) {
                    return (
                      <td
                        key={columnIdx}
                        className={getColumnClassName(column)}
                      >
                        {column.render(data[column.key], data, columnIdx)}
                      </td>
                    );
                  }
                  if (column.key in data) {
                    return <td key={column.key}>{data[column.key]}</td>;
                  }
                  return null;
                })}
                <td className="payment-method-action-column">
                  {isPrimaryPaymentMethod(data, defaultPaymentMethod?.id) ? (
                    <span>
                      <CIcon
                        name="cis-check-circle"
                        alt="Active"
                        style={{ margin: "-4px 6px 0 0" }}
                      />
                      Default
                    </span>
                  ) : (
                    <CButton onClick={() => togglePrimaryConfirm(data)}>
                      Make Default
                    </CButton>
                  )}
                </td>
                {isOwnerAutopayEnrolled && (
                  <td className="payment-method-action-column">
                    {data.id === autopayPaymentMethodId ? (
                      <span>
                        <CIcon
                          name="cis-check-circle"
                          alt="AutoPay"
                          style={{ margin: "-4px 6px 0 0" }}
                        />
                        AutoPay
                      </span>
                    ) : (
                      <PaymentMethodActionTooltip
                        content={getAutopayPaymentMethodIneligibilityReason(data)}
                      >
                        <CButton
                          disabled={!isEligibleDefaultPaymentMethod(data)}
                          onClick={() => toggleAutopayChangeConfirm(data)}
                        >
                          Use for AutoPay
                        </CButton>
                      </PaymentMethodActionTooltip>
                    )}
                  </td>
                )}
                <td className="payment-method-action-column">
                  <CButton onClick={() => toggleRemoveConfirm(data)}>
                    Remove
                  </CButton>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <CModal
        show={!!removeConfirm}
        onClose={() => {
          if (!isRemoveSubmitting) {
            toggleRemoveConfirm(false);
          }
        }}
        closeOnBackdrop={!isRemoveSubmitting}
      >
        <CModalHeader>
          <strong>Confirm payment method removal</strong>
        </CModalHeader>
        <CModalBody>
          {requiresReplacement && (
            <ModalWarningAlert title="Removing default payment method" className="mb-3">
              This is your default payment method. Please choose a replacement.
            </ModalWarningAlert>
          )}
          {showLastPaymentMethodWarning && (
            <ModalWarningAlert title="Removing last payment method" className="mb-3">
              This is your only payment method on file.
            </ModalWarningAlert>
          )}
          {showAutopayDisableWarning && (
            <ModalWarningAlert
              title={
                onlyActiveAccountAffected
                  ? "AutoPay notice"
                  : "AutoPay on other service accounts"
              }
              className="mb-3"
            >
              {onlyActiveAccountAffected ? (
                <>
                  Removing this payment method will disable AutoPay for this
                  service account.
                  {isLastPaymentMethod &&
                    " AutoPay may be re-enabled at any time after adding a new payment method."}
                  {!isLastPaymentMethod &&
                    " AutoPay may be re-enabled at any time."}
                </>
              ) : (
                <>
                  Removing this payment method will affect AutoPay on other
                  service accounts
                  {showDefaultReassignAutopayCheckbox ||
                  showNonDefaultAutopayReassignment
                    ? ". Review the affected accounts below."
                    : (
                      <>
                        : <strong>{affectedAccountsLabel}</strong>.
                        {!requiresReplacement &&
                          " AutoPay will be turned off on those accounts unless you reassign it below."}
                      </>
                    )}
                </>
              )}
            </ModalWarningAlert>
          )}
          {requiresReplacement && (
            <CFormGroup className={showReplacementPmError ? "error" : ""}>
              <CLabel htmlFor="replacement-payment-method">
                Replacement default payment method
              </CLabel>
              {canReplaceDefault ? (
                <CSelect
                  id="replacement-payment-method"
                  value={replacementPmId}
                  disabled={isRemoveSubmitting}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setReplacementPmId(e.target.value);
                    setReplacementPmTouched(true);
                  }}
                  onBlur={() => setReplacementPmTouched(true)}
                >
                  <option value="">Select a payment method</option>
                  {replacementPaymentMethodOptions}
                </CSelect>
              ) : (
                <p className="text-danger mb-0">
                  Add an active, non-expired payment method before removing your
                  default.
                </p>
              )}
              <div className="error-text">
                {showReplacementPmError
                  ? "Please select a payment method"
                  : null}
              </div>
            </CFormGroup>
          )}
          {showDefaultReassignAutopayCheckbox && (
            <CFormGroup className="mb-3">
              <CLabel>
                {formatAffectedServiceAccountsLabel(
                  accountsUsingPmForAutopay.length
                )}
              </CLabel>
              <ModalAccountList accounts={accountsUsingPmForAutopay} />
              <div className="form-check">
                <CInputCheckbox
                  id="reassign-autopay-on-remove"
                  checked={reassignAutopayChecked}
                  disabled={isRemoveSubmitting}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setReassignAutopayChecked(e.target.checked)
                  }
                />
                <CLabel htmlFor="reassign-autopay-on-remove">
                  Reassign AutoPay on these accounts to{" "}
                  {replacementPaymentMethod ? (
                    <PaymentMethodSourceLabel
                      paymentMethod={replacementPaymentMethod}
                    />
                  ) : null}
                </CLabel>
              </div>
              <p className="text-muted mb-0 mt-2">
                If unchecked, AutoPay will be turned off on those accounts when
                this payment method is removed.
              </p>
            </CFormGroup>
          )}
          {showNonDefaultAutopayReassignment && (
            <CFormGroup className="mb-3">
              <CLabel>
                {formatAffectedServiceAccountsLabel(
                  accountsUsingPmForAutopay.length
                )}
              </CLabel>
              <ModalAccountList accounts={accountsUsingPmForAutopay} />
              <CLabel htmlFor="autopay-reassignment-payment-method">
                Reassign AutoPay on affected accounts to
              </CLabel>
              <CSelect
                id="autopay-reassignment-payment-method"
                value={autopayReassignmentPmId}
                disabled={isRemoveSubmitting}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setAutopayReassignmentPmId(e.target.value)
                }
              >
                <option value="">
                  Do not reassign (AutoPay will be disabled)
                </option>
                {replacementPaymentMethodOptions}
              </CSelect>
              {autopayReassignmentPmId ? (
                <p className="text-muted mb-0 mt-2">
                  AutoPay on affected accounts will be moved to{" "}
                  {autopayReassignmentPaymentMethod ? (
                    <PaymentMethodSourceLabel
                      paymentMethod={autopayReassignmentPaymentMethod}
                    />
                  ) : null}{" "}
                  before this payment method is removed.
                </p>
              ) : (
                <p className="text-muted mb-0 mt-2">
                  AutoPay will be turned off on affected accounts when this
                  payment method is removed.
                </p>
              )}
            </CFormGroup>
          )}
          {removeConfirm && (
            <>
              {requiresReplacement ? (
                replacementPmId ? (
                  <p className="mb-3">
                    Remove this payment method and set a new default for one-time
                    payments?
                  </p>
                ) : (
                  <p className="mb-3">
                    Select a replacement default payment method to continue.
                  </p>
                )
              ) : (
                <p className="mb-3">
                  Are you sure you want to remove this payment method?
                </p>
              )}
              <PaymentMethodConfirmSummary
                sections={[
                  {
                    label: "Removed payment method",
                    content: (
                      <PaymentMethodSourceLabel paymentMethod={removeConfirm} />
                    ),
                  },
                  ...(replacementPmId && replacementPaymentMethod
                    ? [
                        {
                          label: "Replacement payment method",
                          content: (
                            <PaymentMethodSourceLabel
                              paymentMethod={replacementPaymentMethod}
                            />
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </>
          )}
        </CModalBody>
        <CModalFooter style={{ display: "flex" }}>
          <div
            style={{ flex: 1, display: "flex" }}
            onClick={!isRemoveFormValid ? touchRemoveFormValidation : undefined}
            role="presentation"
          >
            <CButton
              color="danger"
              style={{
                flex: 1,
                width: "100%",
                ...(!isRemoveFormValid || isRemoveSubmitting
                  ? { pointerEvents: "none" }
                  : {}),
              }}
              disabled={!isRemoveFormValid || isRemoveSubmitting}
              onClick={async () => await handleRemoveConfirm()}
            >
              {isRemoveSubmitting && (
                <CSpinner
                  size="sm"
                  style={{ marginRight: 8, verticalAlign: "middle" }}
                />
              )}
              {getRemoveConfirmButtonLabel()}
            </CButton>
          </div>
          <CButton
            color="secondary"
            style={{ flex: 1 }}
            disabled={isRemoveSubmitting}
            onClick={() => toggleRemoveConfirm(false)}
          >
            Cancel
          </CButton>
        </CModalFooter>
      </CModal>

      {autopayChangeConfirm && (
        <ChangePaymentConfirmModal
          paymentMethod={autopayChangeConfirm}
          resolve={async () => {
            await handleAutopayChangeConfirm();
          }}
          reject={() => {
            if (!isAutopayChangeSubmitting) {
              toggleAutopayChangeConfirm(false);
            }
          }}
        />
      )}

      <CModal
        show={!!primaryConfirm}
        onClose={() => {
          if (!isPrimarySubmitting) {
            togglePrimaryConfirm(false);
          }
        }}
        closeOnBackdrop={!isPrimarySubmitting}
      >
        <CModalHeader>
          <strong>Confirm default payment method</strong>
        </CModalHeader>
        <CModalBody>
          <ModalInfoAlert className="mb-3">
            Default payment method designation is used for one-time payments
            only.
          </ModalInfoAlert>
          Are you sure you would like to set{" "}
          <PaymentMethodSourceLabel paymentMethod={primaryConfirm} /> as your
          default payment method for one-time payments?
        </CModalBody>
        <CModalFooter style={{ display: "flex" }}>
          <CButton
            color="primary"
            style={{ flex: 1 }}
            disabled={isPrimarySubmitting}
            onClick={async () => await handlePrimaryConfirm()}
          >
            {isPrimarySubmitting && (
              <CSpinner
                size="sm"
                style={{ marginRight: 8, verticalAlign: "middle" }}
              />
            )}
            Make Default
          </CButton>
          <CButton
            color="secondary"
            style={{ flex: 1 }}
            disabled={isPrimarySubmitting}
            onClick={() => togglePrimaryConfirm(false)}
          >
            Cancel
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};
